/**
 * Profit & Loss — /finance/reports/pl
 *
 * Shows revenue, cost, and profitability for a chosen period.
 * Calls GET /finance/reports/profit-loss
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, RefreshCw, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { downloadAuthenticatedFile } from './report-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlData {
  reportType: string;
  period: { from: string; to: string };
  revenue: number;
  costOfSales: number;
  grossProfit: number;
  grossMargin: string;
  operatingExpenses: number;
  netProfit: number;
  netMargin: string;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear() {
  const now  = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

// ── P&L row component ─────────────────────────────────────────────────────────

function PlRow({
  label, amount, indent = false, bold = false, positive = true, sub = false,
}: {
  label: string; amount: number; indent?: boolean; bold?: boolean; positive?: boolean; sub?: boolean;
}) {
  const isNeg = amount < 0;
  const colorClass = bold
    ? (isNeg ? 'text-red-700' : 'text-green-700')
    : 'text-foreground';
  return (
    <TableRow className={`border-b ${bold ? 'bg-muted/40 font-semibold text-sm' : 'text-sm'}`}>
      <TableCell className={`py-2.5 ${indent ? 'pl-8' : 'pl-4'} ${sub ? 'text-muted-foreground' : ''}`}>{label}</TableCell>
      <TableCell className={`py-2.5 pr-4 text-right font-mono ${colorClass}`}>
        {positive ? fmt(amount) : `(${fmt(Math.abs(amount))})`}
      </TableCell>
      <TableCell className="pr-4 text-right text-muted-foreground text-xs">—</TableCell>
    </TableRow>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PlPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState<PlData | null>(null);
  const [from, setFrom]       = useState(thisYear().from);
  const [to, setTo]           = useState(thisYear().to);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      const res = await api.get<PlData>(`/finance/reports/profit-loss?from=${f}&to=${t}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    loadedRef.current = false;
    fetchData(from, to);
  }, [fetchData, from, to]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to);
  }, [fetchData, from, to]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-green-600" />
          <div>
            <h1 className="text-xl font-semibold">Profit & Loss</h1>
            <p className="text-sm text-muted-foreground">Income statement for the selected period</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <input type="date" value={to} onChange={e => { setTo(e.target.value); loadedRef.current = false; }}
            className="border rounded px-2 py-1 text-sm" />
          <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadAuthenticatedFile(`/finance/reports/profit-loss/export.xlsx?from=${from}&to=${to}`, `pl-${from}-${to}.xlsx`)}>
            <Download className="h-4 w-4 mr-2" /> XLSX
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Revenue',      value: data.revenue,            color: 'text-green-700' },
            { label: 'Gross Profit', value: data.grossProfit,         color: data.grossProfit >= 0 ? 'text-green-700' : 'text-red-600' },
            { label: 'Net Profit',   value: data.netProfit,           color: data.netProfit   >= 0 ? 'text-green-700' : 'text-red-600' },
            { label: 'Net Margin',   value: data.netMargin as unknown as number, raw: data.netMargin, color: 'text-blue-700' },
          ].map(k => (
            <Card key={k.label} className="p-3">
              <div className="text-xs text-muted-foreground mb-1">{k.label}</div>
              <div className={`text-lg font-bold ${k.color}`}>
                {'raw' in k ? k.raw : fmt(k.value as number)}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* P&L Statement */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading…</div>
          ) : !data ? (
            <div className="text-center py-12 text-muted-foreground">No data for this period.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-sm">Line Item</TableHead>
                  <TableHead className="text-right text-sm pr-4">Amount (SGD)</TableHead>
                  <TableHead className="text-right text-sm pr-4">vs Prior</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <PlRow label="Revenue"             amount={data.revenue}            bold />
                <PlRow label="Cost of Sales"       amount={data.costOfSales}        indent positive={false} />
                <PlRow label="Gross Profit"        amount={data.grossProfit}        bold />
                <TableRow className="bg-muted/20">
                  <TableCell className="pl-4 text-xs text-muted-foreground italic" colSpan={3}>
                    Gross Margin: {data.grossMargin}
                  </TableCell>
                </TableRow>
                <PlRow label="Operating Expenses"  amount={data.operatingExpenses}  indent positive={false} />
                <PlRow label="Net Profit / (Loss)" amount={data.netProfit}          bold />
                <TableRow className="bg-muted/20">
                  <TableCell className="pl-4 text-xs text-muted-foreground italic" colSpan={3}>
                    Net Margin: {data.netMargin}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
